import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface Project {
  id: number
  name: string
  key: string | null
  description: string | null
  project_type: string
  status: string
  start_date: string | null
  end_date: string | null
  customer_id: number
  hour_cap: number | null
  default_hourly_rate: number | null
}

interface Props {
  [key: string]: unknown
  project: Project
  customers: { id: number; name: string }[]
}

const PROJECT_TYPES = [
  { value: "fixed_price", label: "Fixed Price" },
  { value: "time_and_materials", label: "Time & Materials" },
]

const PROJECT_STATUSES = [
  { value: "planning", label: "Planning" },
  { value: "active", label: "Active" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
]

export default function ProjectEdit() {
  const { project, customers } = usePage<Props>().props

  const { data, setData, put, processing, errors } = useForm({
    customer_id: String(project.customer_id),
    name: project.name,
    key: project.key || "",
    project_type: project.project_type,
    status: project.status,
    start_date: project.start_date || "",
    end_date: project.end_date || "",
    description: project.description || "",
    hour_cap: project.hour_cap != null ? String(project.hour_cap) : "",
    default_hourly_rate: project.default_hourly_rate != null ? String(project.default_hourly_rate) : "",
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    put(`/projects/${project.id}`)
  }

  return (
    <>
      <Head title={`Edit ${project.name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/projects">Projects</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/projects/${project.id}`}>{project.name}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/projects/${project.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit Project</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Customer</Label>
                      <Select value={data.customer_id} onValueChange={(v) => setData("customer_id", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select customer..." />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Project Type</Label>
                      <Select value={data.project_type} onValueChange={(v) => setData("project_type", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PROJECT_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input id="name" value={data.name} onChange={(e) => setData("name", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="key">Key</Label>
                      <Input
                        id="key"
                        value={data.key}
                        onChange={(e) => setData("key", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                        className="font-mono"
                        placeholder="auto-generated"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={data.status} onValueChange={(v) => setData("status", v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="start_date">Start Date</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={data.start_date}
                        onChange={(e) => setData("start_date", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end_date">
                        End Date <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={data.end_date}
                        onChange={(e) => setData("end_date", e.target.value)}
                      />
                    </div>
                  </div>

                  {data.project_type === "time_and_materials" && (
                    <>
                      <Separator />
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="hour_cap">
                            Hour cap{" "}
                            <span className="text-muted-foreground font-normal">(optional, total hours billable)</span>
                          </Label>
                          <Input
                            id="hour_cap"
                            type="number"
                            step="0.5"
                            min="0"
                            value={data.hour_cap}
                            onChange={(e) => setData("hour_cap", e.target.value)}
                            placeholder="e.g. 480"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="default_hourly_rate">
                            Default hourly rate{" "}
                            <span className="text-muted-foreground font-normal">(optional fallback)</span>
                          </Label>
                          <Input
                            id="default_hourly_rate"
                            type="number"
                            step="0.01"
                            min="0"
                            value={data.default_hourly_rate}
                            onChange={(e) => setData("default_hourly_rate", e.target.value)}
                            placeholder="e.g. 75.00"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="description">
                      Description <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <textarea
                      id="description"
                      rows={3}
                      className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      value={data.description}
                      onChange={(e) => setData("description", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href={`/projects/${project.id}`}>Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

ProjectEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
